import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { AvatarUpload } from "@features/settings/SettingsProfile/AvatarUpload";
import { useMyself } from "@api/v1/hooks/useMyself";
import { ProfileForm } from "@features/settings/SettingsProfile/ProfileForm";
import { SettingsLayout } from "@features/settings/components/SettingsLayout";
import { useChangeEmail, useUserProfile } from "@rentablo/api/apiv1";

export const SettingsProfilePage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: user } = useMyself();
  const { data: userProfile } = useUserProfile();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const emailChangeToken = searchParams.get("emailChangeToken");
  const { mutate: confirmEmailChange } = useChangeEmail();

  useEffect(() => {
    if (!emailChangeToken) return;

    confirmEmailChange(
      { emailChangeToken },
      {
        onSuccess: () => {
          enqueueSnackbar(t("settings.profile.emailChangeSuccess"), {
            variant: "success",
          });
          queryClient.invalidateQueries({
            queryKey: ["myself"],
          });
          navigate("/settings/profile");
        },
        onError: () => {
          enqueueSnackbar(t("settings.profile.emailChangeError"), {
            variant: "error",
          });
        },
      }
    );
  }, [
    emailChangeToken,
    confirmEmailChange,
    enqueueSnackbar,
    t,
    queryClient,
    navigate,
  ]);

  return (
    <SettingsLayout
      breadcrumbs={[
        { text: t("settings.sidebar.title") },
        { text: t("settings.sidebar.common.profile") },
      ]}
      title={t("settings.profile.title")}
      subtitle={t("settings.profile.subtitle")}
    >
      {user && userProfile ? (
        <Box>
          <AvatarUpload />
          <Divider />
          <ProfileForm
            user={{
              id: user.profileReference?.id,
              firstName: user.profileReference?.firstName,
              lastName: user.profileReference?.lastName,
              academicTitle: userProfile?.academicTitle,
              gender: user.gender === 2 ? "female" : "male",
              email: user.email,
            }}
          />
        </Box>
      ) : null}
    </SettingsLayout>
  );
};
